import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ClipboardList,
  Loader2,
  ChevronRight,
  CheckCircle,
  Clock,
  Home,
} from 'lucide-react';
import { format } from 'date-fns';
import { formatTimeFromDate } from '../../utils/dateFormatting';

const MobileForms = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api, operator, tenantSettings } = useMobileAuth();
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'available');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [templatesRes, submissionsRes] = await Promise.all([
          api.get('/api/form-templates'),
          api.get('/api/form-submissions?mine=true')
        ]);
        
        // Filter only standalone templates that are active
        const standaloneTemplates = templatesRes.data.filter(
          t => t.is_active && t.form_type === 'standalone'
        );
        setTemplates(standaloneTemplates);
        
        // Server already filters by the current operator's identity
        setSubmissions(submissionsRes.data);
      } catch (error) {
        console.error('Failed to fetch forms data:', error);
        toast.error('Failed to load forms');
      } finally {
        setLoading(false);
      }
    };

    if (operator) {
      fetchData();
    }
  }, [api, operator]);

  const handleStartForm = (template) => {
    navigate(`/mobile/forms/submit?templateId=${template.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--m-bg)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  return (
    <MobileLayout>
    <div className="min-h-full bg-[var(--m-bg)]">
      {/* Header */}
      <div className="bg-[var(--m-bg-card)] px-4 py-3 flex items-center justify-between sticky top-0 z-10 safe-top">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/mobile/dashboard')} className="text-[var(--m-text-secondary)] p-1">
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-[#ffd108]" />
            <h1 className="text-[var(--m-text)] font-semibold">Forms</h1>
          </div>
        </div>
        <button 
          onClick={() => navigate('/mobile/dashboard')} 
          className="text-[var(--m-text-secondary)] p-1"
          data-testid="home-btn"
        >
          <Home className="h-5 w-5" />
        </button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full bg-[var(--m-bg-card)] rounded-none border-b border-[var(--m-border)]">
          <TabsTrigger 
            value="available" 
            className="flex-1 data-[state=active]:bg-[#ffd108] data-[state=active]:text-black"
          >
            Available
          </TabsTrigger>
          <TabsTrigger 
            value="submitted" 
            className="flex-1 data-[state=active]:bg-[#ffd108] data-[state=active]:text-black"
          >
            Submitted ({submissions.length})
          </TabsTrigger>
        </TabsList>

        {/* Available Forms Tab - Standalone Forms Only */}
        <TabsContent value="available" className="p-4">
          {templates.length > 0 ? (
            <div className="space-y-2">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleStartForm(template)}
                  className="w-full p-4 bg-[var(--m-bg-card)] rounded-lg text-left hover:bg-[var(--m-bg-elevated)] transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-[var(--m-text)] font-medium">{template.name}</h3>
                      {template.description && (
                        <p className="text-sm text-[var(--m-text-secondary)] mt-1">{template.description}</p>
                      )}
                    </div>
                    <ChevronRight className="h-5 w-5 text-[var(--m-text-secondary)]" />
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center text-[var(--m-text-muted)] py-12">
              <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No standalone forms available</p>
              <p className="text-sm mt-1">Job-related forms can be accessed from Job Details</p>
            </div>
          )}
        </TabsContent>

        {/* Submitted Forms Tab */}
        <TabsContent value="submitted" className="p-4">
          {submissions.length === 0 ? (
            <div className="text-center text-[var(--m-text-muted)] py-12">
              <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No submissions yet</p>
              <p className="text-sm mt-1">Your completed forms will appear here</p>
            </div>
          ) : (
            <div className="space-y-2">
              {submissions.map((submission) => (
                <button
                  key={submission.id}
                  onClick={() => navigate(`/mobile/forms/submission/${submission.id}`)}
                  className="w-full p-4 bg-[var(--m-bg-card)] rounded-lg text-left hover:bg-[var(--m-bg-elevated)] transition-colors"
                  data-testid={`submission-card-${submission.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[var(--m-text)] font-medium">{submission.template_name}</h3>
                      {submission.job_description && (
                        <p className="text-sm text-[var(--m-text-secondary)] mt-1">
                          Job: {submission.job_description}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <Badge 
                          className={submission.status === 'submitted' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-yellow-500/20 text-yellow-400'
                          }
                        >
                          {submission.status === 'submitted' ? (
                            <><CheckCircle className="h-3 w-3 mr-1" /> Submitted</>
                          ) : (
                            <><Clock className="h-3 w-3 mr-1" /> Draft</>
                          )}
                        </Badge>
                        <span className="text-xs text-[var(--m-text-muted)]">
                          {format(new Date(submission.submitted_at || submission.created_at), 'dd/MM/yyyy')} {formatTimeFromDate(new Date(submission.submitted_at || submission.created_at), tenantSettings.time_format)}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-[var(--m-text-secondary)] flex-shrink-0 mt-1" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
    </MobileLayout>
  );
};

export default MobileForms;
